/**
 * Bill of Lading Header Fields Component
 *
 * Renders the header section of the B/L form, grouped into logical sub-sections:
 * - Document Info (reference, type, status, version)
 * - Carrier (code, name, list provider, issuing party)
 * - Dates (issue date, issue place, shipped on board, received for shipment)
 * - Payment Terms (freight, origin charges, destination charges)
 * - Contract (service contract ref, quotation ref, booking ref, SI ref)
 * - Cargo Movement (receipt/delivery types, cargo movement types)
 * - Values (declared value/currency, originals, copies)
 * - Other (pre-carriage, Incoterms, terms and conditions textarea)
 */

import Input from '../common/Input';
import { getFieldIssues } from '../../utils/confidenceUtils';

/**
 * Severity color map — same as OBHeaderFields
 */
const SEVERITY_COLORS = {
  error:   { border: 'border-l-red-500',   text: 'text-red-600',   icon: 'text-red-500' },
  warning: { border: 'border-l-amber-500', text: 'text-amber-600', icon: 'text-amber-500' },
  info:    { border: 'border-l-blue-500',  text: 'text-blue-600',  icon: 'text-blue-500' },
};

function formatIssueType(type) {
  if (!type) return 'Issue';
  return type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

/**
 * Inline issue indicator for select fields (identical pattern to OBHeaderFields)
 */
function IssueIndicator({ issues = [] }) {
  if (issues.length === 0) return null;
  const worst = issues.some(i => i.severity === 'error') ? 'error'
    : issues.some(i => i.severity === 'warning') ? 'warning' : 'info';
  return (
    <span className="relative group/issues inline-flex">
      <svg className={`w-3.5 h-3.5 ${SEVERITY_COLORS[worst]?.icon || 'text-amber-500'} cursor-help`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
      <div className="absolute left-0 top-full mt-1 z-50 hidden group-hover/issues:block w-64">
        <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
          {issues.map((issue, idx) => (
            <div key={idx} className={`px-3 py-2 border-l-4 ${SEVERITY_COLORS[issue.severity]?.border || 'border-l-gray-300'} ${idx > 0 ? 'border-t border-gray-100' : ''}`}>
              <p className={`text-xs font-semibold ${SEVERITY_COLORS[issue.severity]?.text || 'text-gray-600'}`}>{formatIssueType(issue.type)}</p>
              {issue.detail && <p className="text-xs text-gray-600 mt-0.5">{issue.detail}</p>}
            </div>
          ))}
        </div>
      </div>
    </span>
  );
}

/**
 * Reusable select field with inline issue indicator
 */
function SelectField({ label, fieldPath, register, registerPath, options, isEditMode, issues = [] }) {
  const fieldIssues = getFieldIssues(issues, fieldPath);
  const hasIssues = fieldIssues.length > 0;
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        <span className="inline-flex items-center gap-1">
          {label}
          <IssueIndicator issues={fieldIssues} />
        </span>
      </label>
      <select
        {...register(registerPath)}
        disabled={!isEditMode}
        className={`
          w-full px-3 py-2 border rounded-md shadow-sm text-sm
          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
          ${hasIssues ? 'border-amber-400 bg-amber-50' : 'border-gray-300'}
          ${!isEditMode ? 'bg-gray-50 text-gray-600' : ''}
        `}
      >
        {options.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Option lists
// ---------------------------------------------------------------------------

const TRANSPORT_DOC_TYPES = [
  { value: '', label: 'Select...' },
  { value: 'BOL', label: 'BOL - Bill of Lading' },
  { value: 'SWB', label: 'SWB - Sea Waybill' }
];

const DOCUMENT_STATUS_OPTIONS = [
  { value: 'DRAFT', label: 'Draft' },
  { value: 'ISSUED', label: 'Issued' },
  { value: 'SURRENDERED', label: 'Surrendered' },
  { value: 'ACCOMPLISHED', label: 'Accomplished' },
  { value: 'VOIDED', label: 'Voided' },
];

const FREIGHT_PAYMENT_TERMS = [
  { value: '', label: 'Select...' },
  { value: 'PRE', label: 'PRE - Prepaid' },
  { value: 'COL', label: 'COL - Collect' }
];

const RECEIPT_DELIVERY_TYPES = [
  { value: '', label: 'Select...' },
  { value: 'CY', label: 'CY - Container Yard' },
  { value: 'SD', label: 'SD - Store Door' },
  { value: 'CFS', label: 'CFS - Container Freight Station' }
];

const CARGO_MOVEMENT_TYPES = [
  { value: '', label: 'Select...' },
  { value: 'FCL', label: 'FCL - Full Container Load' },
  { value: 'LCL', label: 'LCL - Less than Container Load' }
];

const INCOTERMS_OPTIONS = [
  { value: '', label: 'Select...' },
  { value: 'EXW', label: 'EXW - Ex Works' },
  { value: 'FCA', label: 'FCA - Free Carrier' },
  { value: 'CPT', label: 'CPT - Carriage Paid To' },
  { value: 'CIP', label: 'CIP - Carriage and Insurance Paid To' },
  { value: 'DAP', label: 'DAP - Delivered at Place' },
  { value: 'DPU', label: 'DPU - Delivered at Place Unloaded' },
  { value: 'DDP', label: 'DDP - Delivered Duty Paid' },
  { value: 'FAS', label: 'FAS - Free Alongside Ship' },
  { value: 'FOB', label: 'FOB - Free on Board' },
  { value: 'CFR', label: 'CFR - Cost and Freight' },
  { value: 'CIF', label: 'CIF - Cost, Insurance and Freight' }
];

/**
 * BLHeaderFields Component
 *
 * @param {Object} props
 * @param {Object} props.control - react-hook-form control
 * @param {Function} props.register - react-hook-form register
 * @param {Function} props.watch - react-hook-form watch
 * @param {boolean} props.isEditMode - Whether fields are editable
 * @param {Array} props.issues - Unified issues array
 */
function BLHeaderFields({ control, register, watch, isEditMode, issues = [], errors = {} }) {
  return (
    <div className="space-y-6">
      {/* ---- Document Info ---- */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 border-b pb-2">
          Document Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
          <Input
            label="Transport Document Reference"
            {...register('header.transportDocumentReference')}
            disabled={!isEditMode}
            issues={getFieldIssues(issues, 'header.transportDocumentReference')}
            placeholder="B/L number"
          />

          <SelectField
            label="Document Type"
            fieldPath="header.transportDocumentTypeCode"
            register={register}
            registerPath="header.transportDocumentTypeCode"
            options={TRANSPORT_DOC_TYPES}
            isEditMode={isEditMode}
            issues={issues}
          />

          <SelectField
            label="Document Status"
            fieldPath="header.documentStatus"
            register={register}
            registerPath="header.documentStatus"
            options={DOCUMENT_STATUS_OPTIONS}
            isEditMode={isEditMode}
            issues={issues}
          />

          <Input
            label="Version"
            {...register('header.version')}
            disabled={!isEditMode}
            issues={getFieldIssues(issues, 'header.version')}
            placeholder="e.g., 1"
          />
        </div>
      </div>

      {/* ---- Carrier ---- */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 border-b pb-2">
          Carrier
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
          <Input
            label="Carrier Code"
            {...register('header.carrierCode')}
            disabled={!isEditMode}
            issues={getFieldIssues(issues, 'header.carrierCode')}
            placeholder="e.g., MAEU"
          />

          <Input
            label="Carrier Name"
            {...register('header.carrierName')}
            disabled={!isEditMode}
            issues={getFieldIssues(issues, 'header.carrierName')}
            placeholder="e.g., Maersk"
          />

          <Input
            label="Carrier Code List Provider"
            {...register('header.carrierCodeListProvider')}
            disabled={!isEditMode}
            issues={getFieldIssues(issues, 'header.carrierCodeListProvider')}
            placeholder="e.g., NMFTA"
          />

          <Input
            label="Issuing Party"
            {...register('header.issuingParty')}
            disabled={!isEditMode}
            issues={getFieldIssues(issues, 'header.issuingParty')}
          />
        </div>
      </div>

      {/* ---- Dates ---- */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 border-b pb-2">
          Dates
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
          <Input
            label="Issue Date"
            type="date"
            {...register('header.issueDate')}
            disabled={!isEditMode}
            issues={getFieldIssues(issues, 'header.issueDate')}
          />

          <Input
            label="Issue Place UN Location Code"
            {...register('header.issuePlaceUNLocationCode')}
            disabled={!isEditMode}
            issues={getFieldIssues(issues, 'header.issuePlaceUNLocationCode')}
            placeholder="e.g., CNSHA"
          />

          <Input
            label="Issue Place Name"
            {...register('header.issuePlaceName')}
            disabled={!isEditMode}
            issues={getFieldIssues(issues, 'header.issuePlaceName')}
            placeholder="e.g., Shanghai"
          />

          <Input
            label="Shipped On Board Date"
            type="date"
            {...register('header.shippedOnBoardDate')}
            disabled={!isEditMode}
            issues={getFieldIssues(issues, 'header.shippedOnBoardDate')}
          />

          <Input
            label="Received for Shipment Date"
            type="date"
            {...register('header.receivedForShipmentDate')}
            disabled={!isEditMode}
            issues={getFieldIssues(issues, 'header.receivedForShipmentDate')}
          />
        </div>
      </div>

      {/* ---- Payment Terms ---- */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 border-b pb-2">
          Payment Terms
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
          <SelectField
            label="Freight Payment Terms"
            fieldPath="header.freightPaymentTermCode"
            register={register}
            registerPath="header.freightPaymentTermCode"
            options={FREIGHT_PAYMENT_TERMS}
            isEditMode={isEditMode}
            issues={issues}
          />

          <Input
            label="Origin Charges Payment Term Code"
            {...register('header.originChargesPaymentTermCode')}
            disabled={!isEditMode}
            issues={getFieldIssues(issues, 'header.originChargesPaymentTermCode')}
            placeholder="e.g., PRE"
          />

          <Input
            label="Destination Charges Payment Term Code"
            {...register('header.destinationChargesPaymentTermCode')}
            disabled={!isEditMode}
            issues={getFieldIssues(issues, 'header.destinationChargesPaymentTermCode')}
            placeholder="e.g., COL"
          />
        </div>
      </div>

      {/* ---- Contract References ---- */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 border-b pb-2">
          Contract References
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
          <Input
            label="Service Contract Reference"
            {...register('header.serviceContractReference')}
            disabled={!isEditMode}
            issues={getFieldIssues(issues, 'header.serviceContractReference')}
          />

          <Input
            label="Contract Quotation Reference"
            {...register('header.contractQuotationReference')}
            disabled={!isEditMode}
            issues={getFieldIssues(issues, 'header.contractQuotationReference')}
          />

          <Input
            label="Carrier Booking Reference"
            {...register('header.carrierBookingReference')}
            disabled={!isEditMode}
            issues={getFieldIssues(issues, 'header.carrierBookingReference')}
          />

          <Input
            label="Shipping Instruction Reference"
            {...register('header.shippingInstructionReference')}
            disabled={!isEditMode}
            issues={getFieldIssues(issues, 'header.shippingInstructionReference')}
          />
        </div>
      </div>

      {/* ---- Cargo Movement ---- */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 border-b pb-2">
          Cargo Movement
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
          <SelectField
            label="Receipt Type at Origin"
            fieldPath="header.receiptTypeAtOrigin"
            register={register}
            registerPath="header.receiptTypeAtOrigin"
            options={RECEIPT_DELIVERY_TYPES}
            isEditMode={isEditMode}
            issues={issues}
          />

          <SelectField
            label="Delivery Type at Destination"
            fieldPath="header.deliveryTypeAtDestination"
            register={register}
            registerPath="header.deliveryTypeAtDestination"
            options={RECEIPT_DELIVERY_TYPES}
            isEditMode={isEditMode}
            issues={issues}
          />

          <SelectField
            label="Cargo Movement at Origin"
            fieldPath="header.cargoMovementTypeAtOrigin"
            register={register}
            registerPath="header.cargoMovementTypeAtOrigin"
            options={CARGO_MOVEMENT_TYPES}
            isEditMode={isEditMode}
            issues={issues}
          />

          <SelectField
            label="Cargo Movement at Destination"
            fieldPath="header.cargoMovementTypeAtDestination"
            register={register}
            registerPath="header.cargoMovementTypeAtDestination"
            options={CARGO_MOVEMENT_TYPES}
            isEditMode={isEditMode}
            issues={issues}
          />
        </div>
      </div>

      {/* ---- Values ---- */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 border-b pb-2">
          Values
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
          <Input
            label="Declared Value"
            type="number"
            {...register('header.declaredValue', { valueAsNumber: true })}
            disabled={!isEditMode}
            issues={getFieldIssues(issues, 'header.declaredValue')}
          />

          <Input
            label="Declared Value Currency"
            {...register('header.declaredValueCurrency')}
            disabled={!isEditMode}
            issues={getFieldIssues(issues, 'header.declaredValueCurrency')}
            placeholder="e.g., USD"
          />

          <Input
            label="Number of Originals"
            type="number"
            {...register('header.numberOfOriginals', { valueAsNumber: true })}
            disabled={!isEditMode}
            issues={getFieldIssues(issues, 'header.numberOfOriginals')}
          />

          <Input
            label="Number of Copies"
            type="number"
            {...register('header.numberOfCopies', { valueAsNumber: true })}
            disabled={!isEditMode}
            issues={getFieldIssues(issues, 'header.numberOfCopies')}
          />
        </div>
      </div>

      {/* ---- Other ---- */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 border-b pb-2">
          Other
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <Input
            label="Pre-Carriage By"
            {...register('header.preCarriageBy')}
            disabled={!isEditMode}
            issues={getFieldIssues(issues, 'header.preCarriageBy')}
            placeholder="Vessel or mode for pre-carriage"
          />

          <SelectField
            label="Incoterms"
            fieldPath="header.incoTerms"
            register={register}
            registerPath="header.incoTerms"
            options={INCOTERMS_OPTIONS}
            isEditMode={isEditMode}
            issues={issues}
          />

          {/* Terms and Conditions — full-width textarea */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <span className="inline-flex items-center gap-1">
                Terms and Conditions
                <IssueIndicator issues={getFieldIssues(issues, 'header.termsAndConditions')} />
              </span>
            </label>
            <textarea
              {...register('header.termsAndConditions')}
              disabled={!isEditMode}
              rows={4}
              className={`
                w-full px-3 py-2 border rounded-md shadow-sm text-sm
                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                ${getFieldIssues(issues, 'header.termsAndConditions').length > 0 ? 'border-amber-400 bg-amber-50' : 'border-gray-300'}
                ${!isEditMode ? 'bg-gray-50 text-gray-600' : ''}
              `}
              placeholder="Standard terms and conditions text..."
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default BLHeaderFields;
